import { useEffect } from 'react';
import {
  Building2,
  RefreshCw,
  ArrowRightLeft,
  Settings2,
  ImageIcon,
  Clock,
  Phone,
  Users,
  Mail,
  Receipt,
  CreditCard,
  FolderSync,
  X,
  ChevronLeft,
  ChevronRight,
  FileUp,
  Smartphone,
  ReceiptText,
  BadgeDollarSign,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSidebarStore } from '@/stores/sidebar-store';

const navItems = [
  { label: 'Businesses', href: '/admin', icon: Building2 },
  { label: 'Phone Numbers', href: '/admin/phone-numbers', icon: Phone },
  { label: 'Customer Import', href: '/admin/customer-import', icon: Users },
  { label: 'X-Sync', href: '/admin/sync', icon: RefreshCw },
  { label: 'Receive / Send Batch', href: '/admin/batch-process', icon: ArrowRightLeft },
  { label: 'Product Images', href: '/admin/common-images', icon: ImageIcon },
  { label: 'App Versions', href: '/admin/pos-app-versions', icon: Smartphone },
  { label: 'Auto PO', href: '/admin/auto-po', icon: Clock },
  { label: 'CC Funding Drive Sync', href: '/admin/cc-funding-drive-sync', icon: FolderSync },
  { label: 'Charges With No Order', href: '/admin/payment-recon', icon: ReceiptText },
  { label: 'Mailing Report', href: '/admin/mailing-report', icon: Mail },
  { label: 'Campaign Usage', href: '/admin/campaign-usage', icon: Receipt },
  { label: 'Payment Providers', href: '/admin/payment-providers', icon: CreditCard },
  { label: 'Fees & Pricing', href: '/admin/fees', icon: BadgeDollarSign },
  { label: 'Global Settings', href: '/admin/settings', icon: Settings2 },
  { label: 'Statements Upload', href: '/admin/statements-upload', icon: FileUp },
];

export function AdminSidebar() {
  const currentPath = window.location.hash.slice(1) || '/admin';
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  const mobileOpen = useSidebarStore((s) => s.mobileOpen);
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);

  // Auto-close on hash change
  useEffect(() => {
    const onHashChange = () => setMobileOpen(false);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [setMobileOpen]);

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'flex h-screen flex-col border-r border-border bg-white transition-all duration-300 ease-in-out',
          collapsed ? 'w-[72px]' : 'w-64',
          'hidden lg:flex',
          mobileOpen && 'fixed inset-y-0 left-0 z-50 flex !w-64',
        )}
      >
        {/* Logo / Brand */}
        <div
          className={cn(
            'flex h-16 items-center border-b border-border',
            collapsed ? 'justify-center px-2' : 'justify-between px-5',
          )}
        >
          {!collapsed && (
            <div className="flex flex-col items-center overflow-hidden">
              <img src="/logo.png" alt="Fintech Admin" className="h-8" />
              <span className="text-[10px] font-medium text-muted-foreground whitespace-nowrap">Fintech Admin</span>
            </div>
          )}
          {/* Toggle button -- visible only on desktop */}
          <button
            type="button"
            onClick={toggle}
            className="hidden lg:inline-flex items-center justify-center rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </button>
          {/* Close button -- visible only on mobile */}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="inline-flex items-center justify-center rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
            aria-label="Close navigation menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className={cn('flex-1 overflow-y-auto py-4', collapsed ? 'px-2' : 'px-3')}>
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.href || currentPath.startsWith(item.href + '/');

              return (
                <li key={item.label}>
                  <a
                    href={`#${item.href}`}
                    title={collapsed ? item.label : undefined}
                    className={cn(
                      'flex items-center rounded-lg py-2 text-sm font-medium transition-colors',
                      collapsed ? 'justify-center px-2' : 'gap-3 px-3',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
}
